import React from 'react'
import { Repeat, Check } from 'lucide-react'
import { money } from '../financial/formatting'
import { monthLabel } from './overview'
import DetailNote from './DetailNote'

// ─────────────────────────────────────────────────────────────────────────────
//   Gastos fijos vigentes en el mes
//
//   Un `RecurringExpense` es una plantilla, no dinero: crear uno no registra
//   ningún asiento y no mueve ningún total. Lo que compra es poder decir
//   "PingTree, septiembre, todavía sin registrar" — que un libro de asientos
//   por sí solo no puede, porque un cargo que nadie apuntó y un cargo que nunca
//   ocurrió se ven idénticos en una lista.
//
//   Vigencia y registro se preguntan los dos al servidor, y ninguno se deduce
//   aquí:
//
//     `?in_force_on=` (el último día del mes) → qué tarifa cubría ese día. Esa
//       ventana es `RecurringExpense.objects.in_force_on()`, y sustituye al
//       split abierto/cerrado en vez de combinarse con él — `include_closed`
//       sobra y además sugeriría que hace algo. Es el MISMO corte que usa
//       `_recurring_unregistered` en `overview.py`, así que las dos mitades de
//       la pantalla comparten una sola definición de "qué estaba vigente".
//
//     `registered_periods` → qué meses ya están reclamados. Lo calcula el
//       backend, y viene en la respuesta de la lista precisamente para que
//       ningún cliente reimplemente la regla de reclamación y alguno la
//       implemente mal.
//
//     `registrable` / `registrable_detail` → si esta fila se puede registrar
//       por `POST /{id}/payments`, y por qué no. También del servidor, y por
//       el mismo motivo elevado a regla: `service.registration_refusal` es el
//       ÚNICO sitio donde se decide, y lo leen a la vez el guardia de la ruta
//       y el serializador. Deducirlo aquí de `category_code` sería decidir la
//       regla por segunda vez, y el modo en que eso falla es que la ruta
//       empiece a rechazar algo y el botón siga ahí semanas.
//
// ─────────────────────────────────────────────────────────────────────────────
//   Las cuotas bancarias son gastos fijos, y NO se registran desde aquí
//
//   First Horizon, Flagstar y BankUnited están en esta lista como cualquier
//   otro cargo: son mensuales, tienen su tarifa y su día, y el mes se debe
//   igual. Lo que no pueden es pasar por el botón, y el 422 que devolvería la
//   ruta no es una tecnicidad.
//
//   Una cuota de préstamo no es una cifra: una parte es interés, que es coste,
//   y otra amortiza capital, que reduce un pasivo y no cuesta nada. Esa ruta
//   escribe un CashEntry y nada más, así que no tiene dónde poner la división,
//   y el gasto de deuda se DERIVA de caja menos capital: sin el LoanPayment al
//   lado, el capital es cero y la cuota entera cuenta como coste. Con las tres
//   juntas eso son ~$2.710 de gasto inventado al mes, que además hunde la
//   posición neta de la pantalla de arriba — y nada parecería roto, que es lo
//   peor que puede tener un error de dinero.
//
//   Por eso el botón no se pinta y en su lugar va una línea que dice que desde
//   aquí no se registra, con el `registrable_detail` del backend detrás. No es
//   un estado deshabilitado: un botón gris invita a insistir y no dice por qué.
//
//   El párrafo estaba impreso entero DENTRO de la tarjeta y son noventa
//   palabras: las tres tarjetas de banco quedaban cuatro veces más altas que
//   sus vecinas y descuadraban la rejilla entera, con lo que el bloque que
//   explica una regla acababa siendo el que rompe la pantalla. Plegado, la
//   tarjeta mide lo que las demás y el motivo sigue estando a un clic — y en el
//   DOM, así que el buscador del navegador lo encuentra igual.
// ─────────────────────────────────────────────────────────────────────────────
//   Un mes, aunque la pantalla mire un rango
//
//   Este panel se ANCLA al mes de foco y no se agrupa por los meses del rango,
//   y es una decisión, no una simplificación:
//
//   "¿está registrado?" es una pregunta por mes con una respuesta por mes —un
//   cargo pagado en junio y olvidado en julio no está ni registrado ni sin
//   registrar sobre junio-julio— así que sobre un rango sólo hay dos formas
//   honestas: agrupar por mes, o anclar a uno. Agrupar aquí no se puede hacer
//   sin mentir: `/corporate/recurring-expenses` sólo acepta `in_force_on` de UN
//   día (`in_force_between` es un manager que usa `overview.py` por dentro y no
//   está expuesto), así que serían doce peticiones para un año, o reimplementar
//   en JS la ventana de vigencia con su `end_date` inclusivo — que es justo la
//   mitad que se hace mal y la que impide que una tarifa sustituida se cobre
//   dos veces.
//
//   Anclado, entonces, y el encabezado NOMBRA el mes y dice de qué ventana es
//   un mes. Lo que no puede pasar es que "registrado" parezca referirse al
//   rango entero, y de eso se encarga el rótulo y no un tooltip.
//
//   Los meses del rango que no son el de foco no se pierden: cada uno sin pago
//   registrado sale como una entrada `recurring_unregistered` de `not_counted`,
//   con su propio `month`, y ese panel las agrupa por mes. Los dos bloques se
//   reparten el trabajo — aquél dice qué meses faltan, éste da el botón para el
//   mes de foco.
// ─────────────────────────────────────────────────────────────────────────────

export default function RecurringExpensesPanel({ rows, month, rangeText, single, loading, denied, onRegister }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ color: 'var(--text-secondary)', flexShrink: 0, marginTop: 1 }}>
          <Repeat size={17} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Standing charges in {monthLabel(month)}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
            The rates in force, each saying whether it has been registered.
          </p>
          {/* Sobre un rango, el rótulo tiene que impedir la lectura de que
              "registrado" abarca la ventana entera. Va aquí, a la vista y en el
              encabezado, no en un `title` que no existe en móvil — pero en una
              línea: era un párrafo de cuatro y el rótulo se perdía dentro. */}
          {!single && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              <strong>{monthLabel(month)} only</strong> — one month of {rangeText}. The others are
              under <strong>Not counted yet</strong> above.
            </p>
          )}
        </div>
      </div>

      {denied ? (
        <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          {denied}
        </p>
      ) : loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 12 }}>
          No standing charge was in force in {monthLabel(month)}.
        </p>
      ) : (
        // Una tarjeta por cargo y las tarjetas en rejilla, no ocho filas a todo
        // lo ancho con la mitad derecha vacía. Cada cargo son cuatro datos
        // cortos —proveedor, categoría y día, importe, y si está registrado— y
        // en fila eso cuesta una línea de scroll por cargo para leer lo que
        // cabe de tres en tres.
        //
        // `auto-fit` porque cuántos cargos hay es un dato: tres en un mes y
        // doce en otro.
        //
        // La clase `cf-recurring-row` se queda aunque ya no sea una fila: es el
        // identificador de UN cargo en el DOM y hay tests que aíslan por ella.
        // Renombrarla no aportaría nada y rompería lo que sí importa.
        <div
          className="cf-recurring"
          style={{
            marginTop: 14, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 10,
          }}
        >
          {rows.map((row) => {
            // La única pregunta que esta pantalla resuelve en cliente, y sólo
            // porque el backend calcula la lista para eso.
            const registered = (row.registered_periods || []).includes(month)
            // Del servidor y nunca inferido de la categoría. El `!== false` es
            // deliberado: sólo un false explícito esconde el botón, así que un
            // backend que todavía no mande el campo se comporta como antes en
            // vez de dejar la pantalla entera sin forma de registrar nada.
            const registrable = row.registrable !== false
            return (
              <div
                key={row.id}
                className="cf-recurring-row"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0,
                  padding: '12px 14px', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', background: 'var(--bg-raised)',
                  // El borde izquierdo repite el estado del mes, así que la
                  // rejilla se barre de un vistazo sin leer ninguna insignia:
                  // verde lo que ya salió, ámbar lo que sigue pendiente.
                  borderLeft: `3px solid ${registered ? 'var(--green)' : 'var(--amber)'}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.vendor_name}</span>
                    {/* Una fila que `in_force_on` devuelve puede estar cerrada:
                        es justo el caso para el que existe el filtro — la
                        tarifa que gobernaba un mes pasado y que se sustituyó
                        después. Decirlo explica por qué el importe no coincide
                        con el que se ve hoy. */}
                    {row.superseded_by_id && (
                      <span
                        className="badge badge-gray"
                        title="This rate was in force for the month shown and has been replaced since"
                      >
                        superseded since
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {row.category_name} · day {row.day_of_month}
                  </div>
                </div>

                {/* `marginTop: auto` deja el pie de todas las tarjetas a la
                    misma altura aunque una lleve la insignia de tarifa
                    sustituida y su vecina no. */}
                <div
                  style={{
                    display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
                    justifyContent: 'space-between', marginTop: 'auto',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600, whiteSpace: 'nowrap', fontSize: 16,
                      // La tarifa se tiñe con el estado del mes: verde cuando
                      // ese dinero ya salió, ámbar mientras sigue siendo lo
                      // que se espera pagar.
                      color: registered ? 'var(--green)' : 'var(--amber)',
                    }}
                  >
                    {money(row.amount)}
                  </span>
                  {/* El estado va agrupado, no suelto junto al importe: con
                      `space-between` y tres hijos, la insignia y el botón se
                      separarían el uno del otro y el pie dejaría de leerse como
                      "esto cuesta X y está así".

                      Verde lo que se movió de verdad, ámbar lo que sigue
                      pendiente. El ámbar va en una insignia y no sólo en el
                      botón porque el estado es la información: un botón dice
                      qué se puede hacer, no qué pasa. */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {registered ? (
                      <span
                        className="badge badge-green"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Check size={12} /> Registered
                      </span>
                    ) : (
                      <>
                        <span className="badge badge-amber">Not registered</span>
                        {/* Sin botón cuando el servidor dice que la ruta lo
                            rechazaría. El motivo va debajo, no aquí: son
                            noventa palabras y en el pie las partiría a dos
                            por línea. */}
                        {registrable && (
                          <button className="btn btn-ghost btn-sm" onClick={() => onRegister(row)}>
                            Register payment
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Una línea que dice que desde aquí no se registra, y el
                    `detail` del backend detrás. Se pinta también cuando el mes
                    ya está registrado: el cargo sigue sin ser registrable por
                    aquí, y decirlo sólo la mitad de los meses convertiría la
                    regla en algo que aparece y desaparece.

                    La frase de entrada es la del propio 422
                    (`RecurringNotRegistrable`), armada con los campos de la
                    fila: `registrable_detail` llega como oración empezada —en
                    minúscula y sin punto— porque el backend la compone así, y
                    soltarla sin cabeza se leería como un fragmento. La regla
                    sigue siendo del servidor; esto es puntuación. */}
                {!registrable && row.registrable_detail && (
                  <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    <DetailNote
                      summary="Not registrable here — why"
                      detail={`${row.vendor_name} is filed under ${row.category_name}, and ${row.registrable_detail}.`}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
